import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';

import { useAiAssistant } from './AiAssistantContext';
import { AiAssistantPanel } from './AiAssistantPanel';
import { DISMISSED_STORAGE_KEY } from './constants';
import { Launcher } from './styles';
import type { EstimateSnapshot } from './types';

// Single mount point for the assistant: a floating launcher plus the slide-in
// drawer. The drawer (and therefore the chat session) is created lazily on the
// first open and then kept mounted so history survives navigation/collapse.
// It auto-opens on each Okta sign-in; closing it is remembered against the token's
// auth_time, so it stays closed across reloads and tabs until the next Okta sign-in
// (app logout alone keeps the SSO session, so it doesn't reset it).
export function AiAssistantWidget() {
  const { authState } = useOktaAuth();
  const isAuthenticated = authState?.isAuthenticated === true;
  const authTime = authState?.idToken?.claims?.auth_time;
  const { isOpen, open, close, estimateSnapshot } = useAiAssistant();
  const [hasOpened, setHasOpened] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !authTime) {
      return;
    }
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(DISMISSED_STORAGE_KEY) === String(authTime);
    } catch {
      // Storage unavailable: fall through and open.
    }
    if (!dismissed) {
      open();
    }
  }, [isAuthenticated, authTime, open]);

  const handleClose = useCallback(() => {
    if (authTime) {
      try {
        localStorage.setItem(DISMISSED_STORAGE_KEY, String(authTime));
      } catch {
        // Storage unavailable: the drawer may reopen on reload.
      }
    }
    close();
  }, [authTime, close]);

  useEffect(() => {
    if (isOpen) {
      setHasOpened(true);
    }
  }, [isOpen]);

  // Keep a ref so useChat gets a stable getter that always reads the latest snapshot.
  const snapshotRef = useRef<EstimateSnapshot>(estimateSnapshot);
  snapshotRef.current = estimateSnapshot;
  const getEstimateSnapshot = useCallback(() => snapshotRef.current, []);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      {!isOpen && (
        <Launcher aria-label="Open AI assistant" onClick={open} type="button">
          <img alt="" height={30} src="/images/icon-ai-assistant.svg" width={30} />
        </Launcher>
      )}
      {hasOpened && (
        <AiAssistantPanel
          getEstimateSnapshot={getEstimateSnapshot}
          onClose={handleClose}
          open={isOpen}
        />
      )}
    </>
  );
}
